/** Connectivity evidence, separate from the failed operation: never replay a write. */
import { newUuid } from '@overlayqa/shared';
import type { NetworkFailureProps } from './networkFailure';

export const NETWORK_DIAGNOSTIC_EVENT = 'api_network_diagnostic';
// Traffic policy: machine-cadence reads can fail together. Share one probe per
// minute per tab, including concurrent failures; keep every original failure.
const PROBE_INTERVAL_MS = 60_000;
// Diagnostic budget only. This never delays or changes the product's retry.
const PROBE_TIMEOUT_MS = 3_000;

type CaptureEvent = (event: string, properties: Record<string, unknown>) => void;
type DiagnosticDependencies = {
  host: string;
  key: string;
  capture: CaptureEvent;
  canCapture: () => boolean;
};

/** Snapshotted at rejection, not when a queued analytics event is delivered. */
export function readBrowserNetworkState(): Record<string, unknown> {
  return {
    failure_observed_at: new Date().toISOString(),
    browser_online: typeof navigator === 'undefined' ? null : navigator.onLine,
    document_visibility: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
  };
}

/**
 * The SDK's capture() returning does not prove delivery. Ask its configured
 * ingestion route for real JSON instead. /decide is a read-only configuration
 * request: the existing e2e/verify-posthog-proxy-live.ts uses the same route to
 * prove the generic /oqi rewrite without manufacturing analytics events.
 * This proves route reachability, NOT delivery of an event or the API's cause.
 */
async function probePostHog(host: string, key: string): Promise<Record<string, unknown>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  let status: number | undefined;
  try {
    const response = await fetch(`${host}/decide?v=3`, {
      method: 'POST',
      credentials: 'omit',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: key, distinct_id: 'network-connectivity-probe' }),
      signal: controller.signal,
    });
    status = response.status;
    if (!response.ok) return { posthog_probe_result: 'http_error', posthog_probe_status: status };
    const body: unknown = await response.json();
    // A SPA fallback can answer 200; require PostHog's configuration shape.
    const valid = body !== null && typeof body === 'object' && !Array.isArray(body)
      && 'featureFlags' in body && typeof body.featureFlags === 'object' && body.featureFlags !== null;
    return { posthog_probe_result: valid ? 'reachable' : 'invalid_response', posthog_probe_status: status };
  } catch {
    return {
      posthog_probe_result: controller.signal.aborted ? 'timeout' : status ? 'invalid_response' : 'network_error',
      ...(status !== undefined ? { posthog_probe_status: status } : {}),
    };
  } finally {
    clearTimeout(timer);
  }
}

export function createNetworkFailureReporter(deps: DiagnosticDependencies) {
  let previous: { id: string; started: number; observedAt: string } | undefined;
  return (properties: NetworkFailureProps): void => {
    const now = performance.now();
    const fresh = !previous || now - previous.started >= PROBE_INTERVAL_MS;
    if (fresh) previous = { id: newUuid(), started: now, observedAt: new Date().toISOString() };
    const incident = previous!;
    const context = {
      ...properties,
      network_diagnostic_id: incident.id,
      network_diagnostic_age_ms: Math.round(now - incident.started),
    };
    deps.capture('api_network_failure', context);
    if (!fresh) return;
    const result = properties.browser_online === false
      ? Promise.resolve({ posthog_probe_result: 'skipped_offline' })
      : probePostHog(deps.host, deps.key);
    // Sign-out, internal-account identification or opt-out can happen while the
    // read is in flight. Recheck the same analytics gate before emitting.
    void result.then((outcome) => {
      if (!deps.canCapture()) return;
      deps.capture(NETWORK_DIAGNOSTIC_EVENT, {
        ...context,
        ...outcome,
        posthog_probe_url: `${deps.host}/decide`,
        posthog_probe_started_at: incident.observedAt,
        posthog_probe_completed_at: new Date().toISOString(),
        posthog_probe_duration_ms: Math.round(performance.now() - now),
        posthog_probe_timeout_ms: PROBE_TIMEOUT_MS,
      });
    }).catch(() => { /* Diagnostics must never affect the failed operation. */ });
  };
}
